/**
 * Reverse-map dashboard visibility ("who can see this dashboard") into a
 * per-user view ("what does this user see").
 *
 * `GET /agents/dashboards-admin` returns visibility per dashboard — an
 * `organizations` grant list and a `userIds` / `users[]` allowlist — and has no
 * per-user query param, so the reverse mapping is derived here.
 *
 * A user sees a dashboard when ANY of these hold (grants are additive):
 * - **public** — no org grant and no user allowlist (or `isPublic`)
 * - **org** — the user's organisation is in `organizations`
 * - **user** — the user's `user_index` is in the allowlist
 */

/** @typedef {'public' | 'org' | 'user'} DashboardAccessReason */

const toNumericId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

/** Org ids from dashboard `organizations` (ids or objects). */
export function getDashboardOrgIds(dashboard) {
  const orgs = dashboard?.organizations;
  if (!Array.isArray(orgs)) return [];
  return orgs
    .map((org) => {
      if (org == null) return null;
      if (typeof org === "number" || typeof org === "string") return toNumericId(org);
      return toNumericId(org?.id ?? org?.organization_id);
    })
    .filter((id) => id != null);
}

/** user_index allowlist from the dashboard admin response. */
export function getDashboardUserIds(dashboard) {
  if (Array.isArray(dashboard?.userIds)) {
    return dashboard.userIds.map(Number).filter((n) => !Number.isNaN(n));
  }
  if (Array.isArray(dashboard?.users)) {
    return dashboard.users
      .map((u) => Number(u?.user_id ?? u?.userId ?? u?.user_index))
      .filter((n) => !Number.isNaN(n));
  }
  return [];
}

/** No org grant and no user allowlist — every user sees it. */
export function isDashboardPublic(dashboard) {
  if (dashboard?.isPublic === true) return true;
  return (
    getDashboardOrgIds(dashboard).length === 0 &&
    getDashboardUserIds(dashboard).length === 0
  );
}

/** Every org the user belongs to — the directory can list them under more than one. */
export function getUserOrgIds(user) {
  const raw = Array.isArray(user?.organizationIds)
    ? user.organizationIds
    : [user?.organizationId];
  return raw.map(toNumericId).filter((id) => id != null);
}

/**
 * Why (or whether) `user` can see `dashboard`.
 *
 * @param {object} dashboard
 * @param {{
 *   userIndex?: number|string,
 *   organizationId?: number|string,
 *   organizationIds?: Array<number|string>,
 * } | null} user
 * @returns {{ visible: boolean, reason: DashboardAccessReason | null }}
 */
export function resolveDashboardAccessForUser(dashboard, user) {
  if (!dashboard || !user) return { visible: false, reason: null };

  const userIndex = toNumericId(user.userIndex);
  if (userIndex != null && getDashboardUserIds(dashboard).includes(userIndex)) {
    return { visible: true, reason: "user" };
  }

  const dashboardOrgIds = getDashboardOrgIds(dashboard);
  if (getUserOrgIds(user).some((orgId) => dashboardOrgIds.includes(orgId))) {
    return { visible: true, reason: "org" };
  }

  if (isDashboardPublic(dashboard)) return { visible: true, reason: "public" };

  return { visible: false, reason: null };
}

/** @param {object[]} dashboards */
export function filterDashboardsVisibleToUser(dashboards, user) {
  const list = Array.isArray(dashboards) ? dashboards : [];
  if (!user) return list;
  return list.filter((d) => resolveDashboardAccessForUser(d, user).visible);
}

/**
 * Per-reason counts for the "visible to <user>" summary line.
 *
 * @returns {{ total: number, user: number, org: number, public: number }}
 */
export function summarizeDashboardAccessForUser(dashboards, user) {
  const counts = { total: 0, user: 0, org: 0, public: 0 };
  for (const dashboard of Array.isArray(dashboards) ? dashboards : []) {
    const { visible, reason } = resolveDashboardAccessForUser(dashboard, user);
    if (!visible || !reason) continue;
    counts.total += 1;
    counts[reason] += 1;
  }
  return counts;
}

/**
 * Short badge copy for why a user sees a dashboard.
 *
 * @param {DashboardAccessReason | null} reason
 * @param {{ orgName?: string } | null} [user]
 */
export function formatDashboardAccessReason(reason, user) {
  if (reason === "user") return "Direct access";
  if (reason === "org") return user?.orgName ? `Via ${user.orgName}` : "Via organisation";
  if (reason === "public") return "Shown to all users";
  return "";
}
